import React, { useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  Filler,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import classes from "./charts.module.css";
import { useSelector } from "react-redux";
import { useMerchantInvoiceDashboardOffline } from "../../../../offline";
import { formatCompactNumber } from "./chartPeriodUtils";

ChartJS.register(
  Filler,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
);

const gradientFill = (topColor, bottomColor) => (context) => {
  const { chart } = context;
  const { ctx, chartArea } = chart;

  if (!chartArea) {
    return bottomColor;
  }

  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
  gradient.addColorStop(0, topColor);
  gradient.addColorStop(1, bottomColor);
  return gradient;
};

const VendorOnboardChart = () => {
  const Data = useSelector((state) => state.loginData.value);
  const auth_token = Data?.auth_token;
  const user_token = Data?.user?.token || Data?.user_token;
  const org_id = Data?.org_id || Data?.user?.org_id || Data?.organization_id;
  const { data: dashboardSummary } = useMerchantInvoiceDashboardOffline(
    auth_token,
    user_token,
    org_id
  );

  const trend = dashboardSummary?.vendor_onboard_trend || {};

  const combinedData = useMemo(() => {
    const initiated = (trend?.initiated || []).map((item) => ({
      date: item?.date,
      count: Number(item?.count || 0),
    }));
    const onboarded = (trend?.onboarded || []).map((item) => ({
      date: item?.date,
      count: Number(item?.count || 0),
    }));

    const allDates = [
      ...new Set([
        ...initiated.map((item) => item.date),
        ...onboarded.map((item) => item.date),
      ]),
    ]
      .filter(Boolean)
      .sort((a, b) => new Date(a) - new Date(b));

    const initiatedMap = Object.fromEntries(
      initiated.map((item) => [item.date, item.count])
    );
    const onboardedMap = Object.fromEntries(
      onboarded.map((item) => [item.date, item.count])
    );

    return allDates.map((date) => ({
      date,
      initiated: initiatedMap[date] || 0,
      onboarded: onboardedMap[date] || 0,
    }));
  }, [trend]);

  const totalInitiated = combinedData.reduce(
    (sum, item) => sum + Number(item?.initiated || 0),
    0
  );
  const totalOnboarded = combinedData.reduce(
    (sum, item) => sum + Number(item?.onboarded || 0),
    0
  );

  const chartData = {
    labels: combinedData.map((item) => item.date),
    datasets: [
      {
        label: "Initiated",
        data: combinedData.map((item) => item.initiated),
        borderColor: "#2563eb",
        backgroundColor: gradientFill(
          "rgba(37, 99, 235, 0.26)",
          "rgba(37, 99, 235, 0.02)"
        ),
        pointBackgroundColor: "#ffffff",
        pointBorderColor: "#2563eb",
        pointBorderWidth: 2,
        pointRadius: 3,
        borderWidth: 3,
        tension: 0.34,
        fill: true,
      },
      {
        label: "Onboarded",
        data: combinedData.map((item) => item.onboarded),
        borderColor: "#16a34a",
        backgroundColor: gradientFill(
          "rgba(22, 163, 74, 0.2)",
          "rgba(22, 163, 74, 0.02)"
        ),
        pointBackgroundColor: "#ffffff",
        pointBorderColor: "#16a34a",
        pointBorderWidth: 2,
        pointRadius: 3,
        borderWidth: 3,
        tension: 0.34,
        fill: true,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: "index",
    },
    plugins: {
      legend: {
        position: "top",
        align: "start",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          color: "#29415f",
          padding: 18,
          font: {
            family: "gilroyMedium",
            size: 13,
          },
        },
      },
      tooltip: {
        backgroundColor: "rgba(9, 20, 42, 0.94)",
        borderColor: "rgba(120, 168, 255, 0.24)",
        borderWidth: 1,
        padding: 14,
        callbacks: {
          label: (context) =>
            `${context.dataset.label}: ${formatCompactNumber(context.parsed?.y || 0)}`,
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          maxTicksLimit: 8,
          color: "#47607f",
          font: {
            family: "gilroyMedium",
            size: 11,
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: "rgba(148, 163, 184, 0.18)",
          drawBorder: false,
        },
        ticks: {
          color: "#47607f",
          precision: 0,
          font: {
            family: "gilroyMedium",
            size: 11,
          },
          callback: (value) => formatCompactNumber(value),
        },
      },
    },
  };

  return (
    <section className={classes.invoiceChartWrapper}>
      <div className={classes.chartHeader}>
        <div>
          <h2 className={classes.chartTitle}>Supplier Onboarding: Initiated vs Onboarded</h2>
          <p className={classes.chartSubtitle}>
            Daily initiated versus onboarded progression across available records
          </p>
        </div>
      </div>

      <div className={classes.chartInsightStrip}>
        <div className={classes.chartInsightItem}>
          <span className={classes.chartInsightLabel}>Initiated</span>
          <strong className={classes.chartInsightValue}>
            {formatCompactNumber(totalInitiated)}
          </strong>
        </div>
        <div className={classes.chartInsightItem}>
          <span className={classes.chartInsightLabel}>Onboarded</span>
          <strong className={classes.chartInsightValue}>
            {formatCompactNumber(totalOnboarded)}
          </strong>
        </div>
      </div>

      <div className={classes.chartContainer}>
        {combinedData.length ? (
          <Line data={chartData} options={options} />
        ) : (
          <div className={classes.chartEmptyState}>
            No onboarding trend data available.
          </div>
        )}
      </div>
    </section>
  );
};

export default VendorOnboardChart;
